import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { db, storage } from '../../firebase';
import { updateDoc, doc, getDocs, collection, query, limit, where, getDoc, startAfter, orderBy } from 'firebase/firestore';
import formFields from './utils/formData';
import Autocomplete from '@mui/material/Autocomplete';
import {
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Skeleton,
  Collapse,
  IconButton,
  Tooltip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { ExpandMore, ExpandLess, Search, Clear } from '@mui/icons-material';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import './styles/Profile.scss';
import PersonIcon from '@mui/icons-material/Person';
import { ImageUploader } from './utils/ImageUploader';
import { useAlert } from './utils/alerts';
import ProvinciasSelect from './utils/ProvinciasSelect.js';
import { fetchClientesYTiendasConCache, fetchAOtherdminData, clearUserCache, clearAdminCache } from './firestoreService';
import { comunidades, provincias, provinciasPorComunidad, getProvinciaFromCodigo, getComunidadFromCodigoProvincia } from "../../zoneData.js"
import adminProfiler from './utils/AdminPerformanceProfiler';
import '@mui/system';


const Profile = () => {
  const { currentUser, currentService } = useContext(AuthContext);
  const [isAllTiendasSelected, setIsAllTiendasSelected] = useState(false);
  const [userData, setUserData] = useState({
    nombre: '',
    apellido: '',
    foto: '',
    puesto: '',
    tipoUsuario: [],
    telefono: '',
    telefonoCorto: '',
    correo: '',
    localizacion: '',
    clientesAsignados: [],
    tiendasAsignadas: [],
    provinciasAsignadas: [],
    comunidadesAsignadas: [],
    // Agregar campos alternativos para compatibilidad
    clientes: [],
    tiendas: [],
    provincias: [],
    comunidades: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isUsingCache, setIsUsingCache] = useState(false);
  const [showAlertComponent, AlertComponent] = useAlert();
  const [file, setFile] = useState(null);
  
  // Estados para UI de secciones expandibles
  const [showClientes, setShowClientes] = useState(false);
  const [showServicios, setShowServicios] = useState(false);
  
  // Estados para lazy loading de clientes
  const [clientesData, setClientesData] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [clientesHasMore, setClientesHasMore] = useState(true);
  const [clientesLastDoc, setClientesLastDoc] = useState(null);
  const [clientesSearchTerm, setClientesSearchTerm] = useState('');
  const [clientesSearchDebounce, setClientesSearchDebounce] = useState('');
  
  // Estados para lazy loading de servicios
  const [serviciosData, setServiciosData] = useState([]);
  const [serviciosLoading, setServiciosLoading] = useState(false);
  const [serviciosHasMore, setServiciosHasMore] = useState(true);
  const [serviciosLastDoc, setServiciosLastDoc] = useState(null);
  const [serviciosSearchTerm, setServiciosSearchTerm] = useState('');
  const [serviciosSearchDebounce, setServiciosSearchDebounce] = useState('');
  
  // Estado para toggle de visualización (Localización/Nombre)
  const [serviciosDisplayMode, setServiciosDisplayMode] = useState('nombre');
  
  const handleServiciosDisplayModeChange = (event, newMode) => {
    if (newMode !== null) {
      setServiciosDisplayMode(newMode);
    }
  };
  
  // Estados para compatibilidad (mantener los originales)
  const [clientes, setClientes] = useState([]);
  const [tiendas, setTiendas] = useState([]);
  const [comunidadesSeleccionadas, setComunidadesSeleccionadas] = useState([]);
  const [provinciasSeleccionadas, setProvinciasSeleccionadas] = useState([]);
  const [isAllClientesSelected, setIsAllClientesSelected] = useState(false);
  const [clientesSeleccionados, setClientesSeleccionados] = useState([]);
  const [tiendasSeleccionadas, setTiendasSeleccionadas] = useState([]);
  
  // Constantes
  const ITEMS_PER_PAGE = 50;
  const SEARCH_DEBOUNCE_MS = 500;
  
  // Referencias para observadores de scroll
  const clientesObserverRef = useRef(null);
  const serviciosObserverRef = useRef(null);
  
  // Usar ref para evitar múltiples cargas
  const dataLoaded = useRef(false);

  const fetchUserData = useCallback(async () => {
    if (currentUser && currentService && !dataLoaded.current) {
      try {
        setIsLoading(true);
        setIsUsingCache(false);
        
        // 📊 Iniciar sesión de profiling
        adminProfiler.startSession('Profile', {
          userId: currentUser?.uid,
          serviceId: currentService?.id,
          userEmail: currentUser?.email
        });
        
        const startTime = Date.now();
        
        // 📊 Medir query a Firestore
        adminProfiler.startMeasurement('01a_QueryFirestore', {
          email: currentUser.email,
          collection: 'admins'
        });
        
        const adminQuery = query(
          collection(db, 'servicios', currentService.id, 'admins'),
          where('correo', '==', currentUser.email),
          limit(1)
        );
        const adminSnapshot = await getDocs(adminQuery);

        adminProfiler.endMeasurement('01a_QueryFirestore', {
          docsFound: adminSnapshot.size,
          empty: adminSnapshot.empty
        });

        if (!adminSnapshot.empty) {
          // 📊 Medir extracción de datos
          adminProfiler.startMeasurement('01b_ExtractData', {
            docSize: JSON.stringify(adminSnapshot.docs[0].data()).length
          });

          const adminData = adminSnapshot.docs[0].data();
          console.log("Datos del administrador:", adminData);

          // Determinar qué campos usar, con preferencia por los originales en los datos
          // Usamos el campo que existe, sea clientes o clientesAsignados
          const clientesData = adminData.clientes || adminData.clientesAsignados || [];
          const tiendasData = adminData.tiendas || adminData.tiendasAsignadas || [];
          const provinciasData = adminData.provincias || adminData.provinciasAsignadas || [];
          const comunidadesData = adminData.comunidades || adminData.comunidadesAsignadas || [];

          // Actualizar userData con todos los campos, tanto originales como alternativos
          const newUserData = {
            nombre: adminData.nombre || '',
            apellido: adminData.apellido || '',
            foto: adminData.foto || '',
            puesto: adminData.puesto || '',
            tipoUsuario: adminData.tipoUsuario || [],
            telefono: adminData.telefono || '',
            telefonoCorto: adminData.telefonoCorto || '',
            correo: adminData.correo || '',
            localizacion: adminData.localizacion || '',
            // Usar ambos formatos para compatibilidad total
            clientesAsignados: clientesData,
            clientes: clientesData,
            tiendasAsignadas: tiendasData,
            tiendas: tiendasData,
            provinciasAsignadas: provinciasData,
            provincias: provinciasData,
            comunidadesAsignadas: comunidadesData,
            comunidades: comunidadesData
          };

          adminProfiler.endMeasurement('01b_ExtractData', {
            loaded: true,
            userType: newUserData.tipoUsuario,
            clientesCount: clientesData.length,
            tiendasCount: tiendasData.length
          });

          console.log("Datos procesados para cargar:", newUserData);
          
          // 📊 Medir renderizado del formulario
          adminProfiler.startMeasurement('02_RenderForm');
          
          // Actualizar userData
          setUserData(newUserData);
          
          // Actualizar estados locales
          setClientesSeleccionados(clientesData);
          setTiendasSeleccionadas(tiendasData);
          setComunidadesSeleccionadas(comunidadesData);
          setProvinciasSeleccionadas(provinciasData);
          
          // Marcar que los datos se han cargado
          dataLoaded.current = true;
          
          // Esperar renderizado
          setTimeout(() => {
            adminProfiler.endMeasurement('02_RenderForm', {
              formFieldsRendered: true
            });
            
            const loadTime = Date.now() - startTime;
            
            // 📊 Finalizar sesión
            adminProfiler.endSession({
              totalClientes: clientesData.length,
              totalTiendas: tiendasData.length,
              loadTime: `${loadTime}ms`
            });
          }, 0);
        }
      } catch (error) {
        console.error('Error al obtener el perfil:', error);
        showAlertComponent('error', 'Error', 'Hubo un problema al cargar los datos del perfil');
        
        // Finalizar sesión con error
        adminProfiler.endSession({
          error: error.message,
          success: false
        });
      } finally {
        setIsLoading(false);
      }
    }
  }, [currentUser, currentService, showAlertComponent]);

  // Función para cargar clientes con Firebase query
  const loadClientes = useCallback(async (searchTerm = '', reset = false) => {
    if (!currentService || clientesLoading) return;
    
    console.log('🔍 Cargando clientes:', { searchTerm, reset, hasMore: clientesHasMore });
    
    if (!reset && !clientesHasMore) return;
    
    setClientesLoading(true);
    
    // 📊 Iniciar medición de carga de clientes
    const measurementName = searchTerm ? '05_SearchClientes' : '03_LoadClientes';
    adminProfiler.startSession(`Profile_${measurementName}`, {
      serviceId: currentService.id,
      searchTerm,
      isSearch: !!searchTerm
    });
    
    adminProfiler.startMeasurement(measurementName, {
      searchTerm,
      reset,
      currentCount: clientesData.length
    });
    
    try {
      const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
      let q;
      
      if (searchTerm.trim()) {
        // BÚSQUEDA EN FIREBASE: Buscar en toda la colección, no solo en datos cargados
        q = query(
          clientesRef,
          where('nombre', '>=', searchTerm.trim()),
          where('nombre', '<=', searchTerm.trim() + '\uf8ff'),
          orderBy('nombre'),
          limit(ITEMS_PER_PAGE),
          ...(reset || !clientesLastDoc ? [] : [startAfter(clientesLastDoc)])
        );
        console.log('🔍 BÚSQUEDA EN FIREBASE por nombre:', searchTerm.trim());
        console.log('🔥 Firebase Query Clientes (Búsqueda):', {
          collection: `servicios/${currentService.id}/clientes`,
          where: [
            { field: 'nombre', operator: '>=', value: searchTerm.trim() },
            { field: 'nombre', operator: '<=', value: searchTerm.trim() + '\uf8ff' }
          ],
          orderBy: 'nombre',
          limit: ITEMS_PER_PAGE,
          startAfter: reset || !clientesLastDoc ? 'N/A' : clientesLastDoc.id
        });
      } else {
        // CARGA INICIAL: Solo los primeros 50 clientes para mostrar
        q = query(
          clientesRef,
          orderBy('nombre'),
          limit(ITEMS_PER_PAGE),
          ...(reset || !clientesLastDoc ? [] : [startAfter(clientesLastDoc)])
        );
        console.log('📋 CARGA INICIAL: Solo primeros 50 clientes');
        console.log('🔥 Firebase Query Clientes (Inicial):', {
          collection: `servicios/${currentService.id}/clientes`,
          orderBy: 'nombre',
          limit: ITEMS_PER_PAGE,
          startAfter: reset || !clientesLastDoc ? 'N/A' : clientesLastDoc.id
        });
      }
      
      console.log('⚡ Ejecutando query de clientes...');
      console.log('🔥 Query completa:', q);
      const snapshot = await getDocs(q);
      console.log('📊 Clientes obtenidos:', snapshot.docs.length);
      console.log('🔥 Documentos Firestore:', snapshot.docs.map(doc => ({ id: doc.id, nombre: doc.data().nombre })));
      
      const newClientes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isAsignado: userData.clientesAsignados?.includes(doc.id) || false
      }));
      
      // Actualizar estado
      if (reset) {
        setClientesData(newClientes);
      } else {
        setClientesData(prev => [...prev, ...newClientes]);
      }
      
      // Actualizar paginación
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];
      setClientesLastDoc(lastDoc);
      setClientesHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
      
      console.log('✅ Clientes cargados:', newClientes.length, 'hasMore:', snapshot.docs.length === ITEMS_PER_PAGE);
      
      // 📊 Finalizar medición
      const measurementName = searchTerm ? '05_SearchClientes' : '03_LoadClientes';
      adminProfiler.endMeasurement(measurementName, {
        clientesLoaded: newClientes.length,
        hasMore: snapshot.docs.length === ITEMS_PER_PAGE,
        total: reset ? newClientes.length : clientesData.length + newClientes.length
      });
      
      // 📊 Finalizar sesión
      adminProfiler.endSession({
        clientesLoaded: newClientes.length,
        isSearch: !!searchTerm,
        searchTerm: searchTerm || 'N/A'
      });
      
    } catch (error) {
      console.error('❌ Error loading clientes:', error);
      console.error('🔥 Error Firebase:', {
        error: error.message,
        code: error.code,
        details: error
      });
      
      // Finalizar sesión con error
      const measurementName = searchTerm ? '05_SearchClientes' : '03_LoadClientes';
      adminProfiler.endMeasurement(measurementName, {
        error: error.message
      });
      adminProfiler.endSession({
        error: error.message,
        success: false
      });
    } finally {
      setClientesLoading(false);
    }
  }, [currentService, clientesLoading, clientesHasMore, clientesLastDoc, userData.clientesAsignados, clientesData.length]);

  // Función para cargar servicios con Firebase query
  const loadServicios = useCallback(async (searchTerm = '', reset = false) => {
    if (!currentService || serviciosLoading) return;
    
    console.log('🔍 Cargando servicios:', { searchTerm, reset, hasMore: serviciosHasMore });
    
    if (!reset && !serviciosHasMore) return;
    
    setServiciosLoading(true);
    
    // 📊 Iniciar medición de carga de servicios
    const measurementName = searchTerm ? '06_SearchServicios' : '04_LoadServicios';
    adminProfiler.startSession(`Profile_${measurementName}`, {
      serviceId: currentService.id,
      searchTerm,
      isSearch: !!searchTerm
    });
    
    adminProfiler.startMeasurement(measurementName, {
      searchTerm,
      reset,
      currentCount: serviciosData.length
    });
    
    try {
      const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
      let snapshots = [];
      
      if (searchTerm.trim()) {
        const searchTermTrimmed = searchTerm.trim().toLowerCase();
        
        // Cargar más datos para búsqueda y filtrar client-side
        const q = query(
          tiendasRef,
          orderBy('numTienda'),
          limit(200)
        );
        
        console.log('🔍 BÚSQUEDA: Cargando servicios para filtrar por:', searchTermTrimmed);
        const snapshot = await getDocs(q);
        
        // Filtrar client-side para búsqueda parcial en múltiples campos
        snapshots = snapshot.docs.filter(doc => {
          const data = doc.data();
          const numTienda = data.numTienda?.toString().toLowerCase() || '';
          const nombreTienda = data.nombreTienda?.toLowerCase() || '';
          const localizacion = data.localizacion?.toLowerCase() || '';
          const nombreCliente = data.nombreCliente?.toLowerCase() || '';
          
          return numTienda.includes(searchTermTrimmed) ||
                 nombreTienda.includes(searchTermTrimmed) ||
                 localizacion.includes(searchTermTrimmed) ||
                 nombreCliente.includes(searchTermTrimmed);
        });
        
        console.log('📊 Servicios filtrados:', snapshots.length, 'de', snapshot.docs.length);
      } else {
        // CARGA INICIAL: Solo los primeros 50 servicios para mostrar
        const q = query(
          tiendasRef,
          orderBy('numTienda'),
          limit(ITEMS_PER_PAGE),
          ...(reset || !serviciosLastDoc ? [] : [startAfter(serviciosLastDoc)])
        );
        console.log('📋 CARGA INICIAL: Solo primeros 50 servicios');
        const snapshot = await getDocs(q);
        snapshots = snapshot.docs;
        
        // Actualizar paginación solo para carga inicial
        const lastDoc = snapshot.docs[snapshot.docs.length - 1];
        setServiciosLastDoc(lastDoc);
        setServiciosHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
      }
      
      console.log('⚡ Ejecutando query de servicios...');
      console.log('📊 Servicios obtenidos:', snapshots.length);
      
      // Deduplicar por id
      const seenIds = new Set();
      const newServicios = snapshots
        .filter(doc => {
          if (seenIds.has(doc.id)) return false;
          seenIds.add(doc.id);
          return true;
        })
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            numTienda: data.numTienda,
            nombreTienda: data.nombreTienda || `Servicio ${data.numTienda}`,
            nombreCliente: data.nombreCliente || 'Cliente desconocido',
            localizacion: data.localizacion || '',
            isAsignado: userData.tiendasAsignadas?.includes(doc.id) || false,
            ...data
          };
        });
      
      // Actualizar estado
      if (reset) {
        setServiciosData(newServicios);
      } else {
        setServiciosData(prev => [...prev, ...newServicios]);
      }
      
      console.log('✅ Servicios cargados:', newServicios.length, 'hasMore:', serviciosHasMore);
      
      // 📊 Finalizar medición
      const measurementName = searchTerm ? '06_SearchServicios' : '04_LoadServicios';
      adminProfiler.endMeasurement(measurementName, {
        serviciosLoaded: newServicios.length,
        hasMore: serviciosHasMore,
        total: reset ? newServicios.length : serviciosData.length + newServicios.length
      });
      
      // 📊 Finalizar sesión
      adminProfiler.endSession({
        serviciosLoaded: newServicios.length,
        isSearch: !!searchTerm,
        searchTerm: searchTerm || 'N/A'
      });
      
    } catch (error) {
      console.error('❌ Error loading servicios:', error);
      console.error('🔥 Error Firebase:', {
        error: error.message,
        code: error.code,
        details: error
      });
      
      // Finalizar sesión con error
      const measurementName = searchTerm ? '06_SearchServicios' : '04_LoadServicios';
      adminProfiler.endMeasurement(measurementName, {
        error: error.message
      });
      adminProfiler.endSession({
        error: error.message,
        success: false
      });
    } finally {
      setServiciosLoading(false);
    }
  }, [currentService, serviciosLoading, serviciosHasMore, serviciosLastDoc, userData.tiendasAsignadas, serviciosData.length]);

  // Cargar clientes cuando se expande la sección
  useEffect(() => {
    if (!currentService || !userData.clientesAsignados) return;
    
    if (showClientes && clientesData.length === 0 && !clientesLoading) {
      console.log('🔄 Sección clientes expandida, cargando datos iniciales...');
      loadClientes('', true);
    }
  }, [showClientes, currentService, userData.clientesAsignados]);

  // Cargar servicios cuando se expande la sección
  useEffect(() => {
    if (!currentService || !userData.tiendasAsignadas) return;
    
    if (showServicios && serviciosData.length === 0 && !serviciosLoading) {
      console.log('🔄 Sección servicios expandida, cargando datos iniciales...');
      loadServicios('', true);
    }
  }, [showServicios, currentService, userData.tiendasAsignadas]);

  // Debounce para búsqueda de clientes
  useEffect(() => {
    console.log('⏱️ Debounce clientes - término original:', clientesSearchTerm);
    const timeout = setTimeout(() => {
      console.log('⏱️ Debounce clientes - aplicando término:', clientesSearchTerm);
      setClientesSearchDebounce(clientesSearchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [clientesSearchTerm]);

  // Debounce para búsqueda de servicios
  useEffect(() => {
    console.log('⏱️ Debounce servicios - término original:', serviciosSearchTerm);
    const timeout = setTimeout(() => {
      console.log('⏱️ Debounce servicios - aplicando término:', serviciosSearchTerm);
      setServiciosSearchDebounce(serviciosSearchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [serviciosSearchTerm]);

  // Ejecutar búsqueda de clientes cuando cambie el término
  useEffect(() => {
    if (!currentService || !userData.clientesAsignados) return;
    
    if (clientesSearchDebounce.trim()) {
      console.log('🔄 Ejecutando búsqueda de clientes:', clientesSearchDebounce);
      loadClientes(clientesSearchDebounce, true);
    }
  }, [clientesSearchDebounce, currentService, userData.clientesAsignados]);

  // Ejecutar búsqueda de servicios cuando cambie el término
  useEffect(() => {
    if (!currentService || !userData.tiendasAsignadas) return;
    
    if (serviciosSearchDebounce.trim()) {
      console.log('🔄 Ejecutando búsqueda de servicios:', serviciosSearchDebounce);
      loadServicios(serviciosSearchDebounce, true);
    }
  }, [serviciosSearchDebounce, currentService, userData.tiendasAsignadas]);

  // Observer para scroll infinito de clientes
  useEffect(() => {
    if (!currentService || !userData.clientesAsignados) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && clientesHasMore && !clientesLoading) {
          console.log('📜 Cargando más clientes por scroll...');
          // Usar la función loadClientes del scope
          const loadMoreClientes = async () => {
            if (!currentService || clientesLoading) return;
            
            setClientesLoading(true);
            try {
              const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
              const q = query(
                clientesRef,
                orderBy('nombre'),
                limit(ITEMS_PER_PAGE),
                startAfter(clientesLastDoc)
              );
              
              console.log('🔥 Firebase Query Clientes (Scroll):', {
                collection: `servicios/${currentService.id}/clientes`,
                orderBy: 'nombre',
                limit: ITEMS_PER_PAGE,
                startAfter: clientesLastDoc.id
              });
              
              const snapshot = await getDocs(q);
              console.log('🔥 Scroll - Clientes obtenidos:', snapshot.docs.length);
              
              const newClientes = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                isAsignado: userData.clientesAsignados?.includes(doc.id) || false
              }));
              
              setClientesData(prev => [...prev, ...newClientes]);
              
              const lastDoc = snapshot.docs[snapshot.docs.length - 1];
              setClientesLastDoc(lastDoc);
              setClientesHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
              
            } catch (error) {
              console.error('❌ Error loading more clientes:', error);
              console.error('🔥 Error Firebase Scroll:', {
                error: error.message,
                code: error.code,
                details: error
              });
            } finally {
              setClientesLoading(false);
            }
          };
          
          loadMoreClientes();
        }
      },
      { threshold: 0.1 }
    );
    
    if (clientesObserverRef.current) {
      observer.observe(clientesObserverRef.current);
    }
    
    return () => {
      if (clientesObserverRef.current) {
        observer.unobserve(clientesObserverRef.current);
      }
    };
  }, [currentService, userData.clientesAsignados, clientesHasMore, clientesLoading, clientesLastDoc]);

  // Observer para scroll infinito de servicios
  useEffect(() => {
    if (!currentService || !userData.tiendasAsignadas) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && serviciosHasMore && !serviciosLoading) {
          console.log('📜 Cargando más servicios por scroll...');
          // Usar la función loadServicios del scope
          const loadMoreServicios = async () => {
            if (!currentService || serviciosLoading) return;
            
            setServiciosLoading(true);
            try {
              const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
              const q = query(
                tiendasRef,
                orderBy('numTienda'),
                limit(ITEMS_PER_PAGE),
                startAfter(serviciosLastDoc)
              );
              
              console.log('🔥 Firebase Query Servicios (Scroll):', {
                collection: `servicios/${currentService.id}/tiendas`,
                orderBy: 'numTienda',
                limit: ITEMS_PER_PAGE,
                startAfter: serviciosLastDoc.id
              });
              
              const snapshot = await getDocs(q);
              console.log('🔥 Scroll - Servicios obtenidos:', snapshot.docs.length);
              
              const newServicios = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  numTienda: data.numTienda,
                  nombreTienda: data.nombreTienda || `Servicio ${data.numTienda}`,
                  nombreCliente: data.nombreCliente || 'Cliente desconocido',
                  isAsignado: userData.tiendasAsignadas?.includes(doc.id) || false,
                  ...data
                };
              });
              
              setServiciosData(prev => [...prev, ...newServicios]);
              
              const lastDoc = snapshot.docs[snapshot.docs.length - 1];
              setServiciosLastDoc(lastDoc);
              setServiciosHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
              
            } catch (error) {
              console.error('❌ Error loading more servicios:', error);
              console.error('🔥 Error Firebase Scroll:', {
                error: error.message,
                code: error.code,
                details: error
              });
            } finally {
              setServiciosLoading(false);
            }
          };
          
          loadMoreServicios();
        }
      },
      { threshold: 0.1 }
    );
    
    if (serviciosObserverRef.current) {
      observer.observe(serviciosObserverRef.current);
    }
    
    return () => {
      if (serviciosObserverRef.current) {
        observer.unobserve(serviciosObserverRef.current);
      }
    };
  }, [currentService, userData.tiendasAsignadas, serviciosHasMore, serviciosLoading, serviciosLastDoc]);

  // Función para asignar TODOS los clientes de la base de datos
  const handleAssignAllClientes = useCallback(async (checked) => {
    if (!currentService) return;
    
    try {
      if (checked) {
        console.log('🚀 Asignando TODOS los clientes de la base de datos...');
        
        // Obtener todos los clientes de la base de datos
        const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
        const q = query(clientesRef, orderBy('nombre'));
        const snapshot = await getDocs(q);
        
        const todosLosClientesIds = snapshot.docs.map(doc => doc.id);
        console.log(`📊 Total de clientes en la base de datos: ${todosLosClientesIds.length}`);
        
        // Actualizar estado del admin
        setUserData(prev => ({
          ...prev,
          clientesAsignados: todosLosClientesIds,
          clientes: todosLosClientesIds
        }));
        
        // Actualizar estado de los datos mostrados
        setClientesData(prev => prev.map(cliente => ({
          ...cliente,
          isAsignado: true
        })));
        
        console.log('✅ Todos los clientes asignados correctamente');
        
      } else {
        console.log('🚀 Desasignando TODOS los clientes...');
        
        // Desasignar todos los clientes
        setUserData(prev => ({
          ...prev,
          clientesAsignados: [],
          clientes: []
        }));
        
        // Actualizar estado de los datos mostrados
        setClientesData(prev => prev.map(cliente => ({
          ...cliente,
          isAsignado: false
        })));
        
        console.log('✅ Todos los clientes desasignados correctamente');
      }
    } catch (error) {
      console.error('❌ Error al asignar todos los clientes:', error);
      alert('Error al asignar todos los clientes. Por favor, intenta de nuevo.');
    }
  }, [currentService]);

  // Función para asignar TODOS los servicios de la base de datos
  const handleAssignAllServicios = useCallback(async (checked) => {
    if (!currentService) return;
    
    try {
      if (checked) {
        console.log('🚀 Asignando TODOS los servicios de la base de datos...');
        
        // Obtener todos los servicios de la base de datos
        const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
        const q = query(tiendasRef, orderBy('numTienda'));
        const snapshot = await getDocs(q);
        
        const todosLosServiciosIds = snapshot.docs.map(doc => doc.id);
        console.log(`📊 Total de servicios en la base de datos: ${todosLosServiciosIds.length}`);
        
        // Actualizar estado del admin
        setUserData(prev => ({
          ...prev,
          tiendasAsignadas: todosLosServiciosIds,
          tiendas: todosLosServiciosIds
        }));
        
        // Actualizar estado de los datos mostrados
        setServiciosData(prev => prev.map(servicio => ({
          ...servicio,
          isAsignado: true
        })));
        
        console.log('✅ Todos los servicios asignados correctamente');
        
      } else {
        console.log('🚀 Desasignando TODOS los servicios...');
        
        // Desasignar todos los servicios
        setUserData(prev => ({
          ...prev,
          tiendasAsignadas: [],
          tiendas: []
        }));
        
        // Actualizar estado de los datos mostrados
        setServiciosData(prev => prev.map(servicio => ({
          ...servicio,
          isAsignado: false
        })));
        
        console.log('✅ Todos los servicios desasignados correctamente');
      }
    } catch (error) {
      console.error('❌ Error al asignar todos los servicios:', error);
      alert('Error al asignar todos los servicios. Por favor, intenta de nuevo.');
    }
  }, [currentService]);

  // UseEffect para cargar datos del usuario, solo una vez
  useEffect(() => {
    if (currentUser && currentService && !dataLoaded.current) {
      fetchUserData();
    }
  }, [currentUser, currentService]);

  const onFileChange = useCallback(async (file) => {
    setFile(file);

    const storageRef = ref(storage, `profile-photos/${currentUser.email}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Opcional: manejar el progreso de la carga
      },
      (error) => {
        console.error('Error al subir la foto:', error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          console.log(downloadURL)
          setUserData((prevData) => ({
            ...prevData,
            foto: downloadURL,
          }));
        });
      }
    );
  }, [currentUser?.email]);

  const handleInputChange = useCallback(async (e) => {
    const { name, value, files } = e.target;

    if (name === 'foto' && files && files.length > 0) {
      const photoFile = files[0];

      try {
        const storageRef = ref(storage, `profile-photos/${currentUser.email}/${photoFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, photoFile);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // Handle upload progress if needed
          },
          (error) => {
            console.error('Error uploading photo:', error);
          },
          () => {
            // On successful upload, get the download URL and update the user data
            getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
              setUserData((prevData) => ({
                ...prevData,
                [name]: downloadURL,
              }));
            });
          }
        );
      } catch (error) {
        console.error('Error uploading photo:', error);
      }
    } else {
      // Handle other input changes
      setUserData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  }, [currentUser?.email]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (currentService && currentUser) {
        // Realizar una consulta para obtener el documento con el correo coincidente
        const adminQuery = query(
          collection(db, 'servicios', currentService.id, 'admins'),
          where('correo', '==', currentUser.email),
          limit(1)
        );

        const adminSnapshot = await getDocs(adminQuery);

        if (!adminSnapshot.empty) {
          // Obtener el documento
          const adminDoc = adminSnapshot.docs[0];

          // Obtener los arrays de tipoUsuario
          const adminTipoUsuario = adminDoc.data().tipoUsuario || [];
          const nuevoTipoUsuario = userData.tipoUsuario || [];

          // Verificar que el nuevo tipo de usuario no sea superior al actual
          if ((adminTipoUsuario.includes('administrador') || adminTipoUsuario.includes('administrativo')) &&
            nuevoTipoUsuario.includes('inspector')) {
            // Verificar qué campos originales existían en el documento
            const originalData = adminDoc.data();
            const updateData = {};

            // Si el campo original era 'clientes', actualizar 'clientes'
            if ('clientes' in originalData) {
              updateData.clientes = userData.clientesAsignados;
            }
            // Si el campo original era 'clientesAsignados', actualizar 'clientesAsignados'
            if ('clientesAsignados' in originalData) {
              updateData.clientesAsignados = userData.clientesAsignados;
            }
            // Si ninguno existía, actualizar ambos
            if (!('clientes' in originalData) && !('clientesAsignados' in originalData)) {
              updateData.clientes = userData.clientesAsignados;
              updateData.clientesAsignados = userData.clientesAsignados;
            }

            // Hacer lo mismo para tiendas, provincias y comunidades
            if ('tiendas' in originalData) {
              updateData.tiendas = userData.tiendasAsignadas;
            }
            if ('tiendasAsignadas' in originalData) {
              updateData.tiendasAsignadas = userData.tiendasAsignadas;
            }
            if (!('tiendas' in originalData) && !('tiendasAsignadas' in originalData)) {
              updateData.tiendas = userData.tiendasAsignadas;
              updateData.tiendasAsignadas = userData.tiendasAsignadas;
            }

            if ('provincias' in originalData) {
              updateData.provincias = userData.provinciasAsignadas;
            }
            if ('provinciasAsignadas' in originalData) {
              updateData.provinciasAsignadas = userData.provinciasAsignadas;
            }
            if (!('provincias' in originalData) && !('provinciasAsignadas' in originalData)) {
              updateData.provincias = userData.provinciasAsignadas;
              updateData.provinciasAsignadas = userData.provinciasAsignadas;
            }

            if ('comunidades' in originalData) {
              updateData.comunidades = userData.comunidadesAsignadas;
            }
            if ('comunidadesAsignadas' in originalData) {
              updateData.comunidadesAsignadas = userData.comunidadesAsignadas;
            }
            if (!('comunidades' in originalData) && !('comunidadesAsignadas' in originalData)) {
              updateData.comunidades = userData.comunidadesAsignadas;
              updateData.comunidadesAsignadas = userData.comunidadesAsignadas;
            }

            // Actualizar el documento
            await updateDoc(adminDoc.ref, updateData);

            // Limpiar caché
            clearUserCache(currentService.id, adminDoc.id);
            clearAdminCache(currentService.id);

            // Mostrar la alerta
            showAlertComponent('success', 'Perfil actualizado', 'Tu perfil ha sido actualizado exitosamente.');

            console.log('Datos guardados:', updateData);
            
            // Recargar los datos para asegurar consistencia
            dataLoaded.current = false;
            await fetchUserData();

            console.log('Perfil actualizado exitosamente');
          } else {
            // Mostrar la alerta de tipo de usuario incorrecto
            showAlertComponent('error', 'Error al actualizar el perfil', 'No puedes cambiar tu tipo de usuario a uno superior.');
          }
        } else {
          console.error('El documento no existe. No se puede actualizar.');
          showAlertComponent('error', 'Error', 'No se encontró el documento del usuario');
        }
      }
    } catch (error) {
      console.error('Error al actualizar el perfil:', error);
      showAlertComponent('error', 'Error', 'Hubo un problema al actualizar el perfil');
    } finally {
      setIsLoading(false);
    }
  }, [currentService, currentUser, userData, showAlertComponent]);

  const handleSelectProvincias = useCallback((provinciasSeleccionadas) => {
    setUserData(prevData => ({
      ...prevData, 
      provinciasAsignadas: provinciasSeleccionadas,
      provincias: provinciasSeleccionadas,
    })); 
  }, []);

  const handleChangeClientes = useCallback((selectedClientes) => {
    // Usar serviciosData en lugar de tiendas para mejor rendimiento
    const nuevasTiendasAsignadas = serviciosData.filter(servicio =>
      selectedClientes.includes(servicio.cliente)
    ).map(servicio => servicio.id);

    setUserData(prevData => ({
      ...prevData,
      clientesAsignados: selectedClientes,
      clientes: selectedClientes,
      tiendasAsignadas: nuevasTiendasAsignadas,
      tiendas: nuevasTiendasAsignadas,
    }));

    // Actualizar estado de asignación en los datos mostrados
    setClientesData(prev => prev.map(cliente => ({
      ...cliente,
      isAsignado: selectedClientes.includes(cliente.id)
    })));
  }, [serviciosData]);

  const handleChangeTiendas = useCallback((selectedServicios) => {
    // Usar serviciosData en lugar de tiendas para mejor rendimiento
    const tiendasIds = selectedServicios.map(numTienda => {
      const servicio = serviciosData.find(s => s.numTienda.toString() === numTienda);
      return servicio ? servicio.id : numTienda;
    });

    setUserData(prevData => ({
      ...prevData,
      tiendasAsignadas: tiendasIds,
      tiendas: tiendasIds,
    }));

    // Actualizar estado de asignación en los datos mostrados
    setServiciosData(prev => prev.map(servicio => ({
      ...servicio,
      isAsignado: tiendasIds.includes(servicio.id)
    })));
  }, [serviciosData]);

  const handleChangeComunidades = useCallback((event) => {
    const nuevasComunidades = event.target.value;

    // Filtrar provincias disponibles basadas en comunidades seleccionadas
    const provinciasDisponibles = nuevasComunidades.reduce((acc, comunidad) => {
      return [...acc, ...(provinciasPorComunidad[comunidad] || [])];
    }, []);

    // Actualizar provincias seleccionadas para incluir solo aquellas disponibles
    const nuevasProvinciasSeleccionadas = userData.provinciasAsignadas?.filter(provincia =>
      provinciasDisponibles.includes(provincia)
    ) || [];

    // Actualizar userData con las nuevas comunidades y provincias (ambos formatos)
    setUserData(prevData => ({
      ...prevData,
      comunidadesAsignadas: nuevasComunidades,
      comunidades: nuevasComunidades,
      provinciasAsignadas: nuevasProvinciasSeleccionadas,
      provincias: nuevasProvinciasSeleccionadas,
    }));
  }, [userData.provinciasAsignadas]);

  // Memoizar datos de clientes mostrados
  const clientesMostrados = useMemo(() => {
    if (!clientesData || clientesData.length === 0) {
      return [];
    }
    
    if (clientesSearchDebounce.trim()) {
      // Si hay búsqueda, mostrar resultados de búsqueda
      return clientesData;
    } else {
      // Si no hay búsqueda, mostrar primero los asignados y luego los demás
      const asignados = clientesData.filter(c => c.isAsignado);
      const noAsignados = clientesData.filter(c => !c.isAsignado);
      return [...asignados, ...noAsignados];
    }
  }, [clientesData, clientesSearchDebounce]);

  // Memoizar datos de servicios mostrados
  const serviciosMostrados = useMemo(() => {
    if (!serviciosData || serviciosData.length === 0) {
      return [];
    }
    
    if (serviciosSearchDebounce.trim()) {
      // Si hay búsqueda, mostrar resultados de búsqueda
      return serviciosData;
    } else {
      // Si no hay búsqueda, mostrar primero los asignados y luego los demás
      const asignados = serviciosData.filter(s => s.isAsignado);
      const noAsignados = serviciosData.filter(s => !s.isAsignado);
      return [...asignados, ...noAsignados];
    }
  }, [serviciosData, serviciosSearchDebounce]);

  // Función de ayuda para determinar qué valores mostrar en los selects
  const getSelectValues = (fieldAsignado, fieldNormal) => {
    // Si hay valores en el campo principal, usar esos
    if (userData[fieldAsignado] && userData[fieldAsignado].length > 0) {
      return userData[fieldAsignado];
    }
    // Si no, usar el campo alternativo
    if (userData[fieldNormal] && userData[fieldNormal].length > 0) {
      return userData[fieldNormal];
    }
    // Si no hay datos en ninguno, devolver array vacío
    return [];
  };

  if (isLoading) {
    return (
      <div className="loading-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
        <p style={{ marginLeft: '10px' }}>Cargando perfil...</p>
      </div>
    );
  }

  // Obtener valores para los selects
  const clientesValues = getSelectValues('clientes', 'clientesAsignados');
  const tiendasValues = getSelectValues('tiendas', 'tiendasAsignadas');
  const comunidadesValues = getSelectValues('comunidades', 'comunidadesAsignadas');

  console.log("Valores para selects:", { 
    clientesValues, 
    tiendasValues, 
    comunidadesValues,
    provinciasAsignadas: userData.provinciasAsignadas
  });



  return (
    <div className="profile-container">
      <div className="user-section">
        <div className="user-header">
          {userData.foto ? (
            <img className="user-photo" src={userData.foto} alt="User" />
          ) : (
            <PersonIcon className="default-user-icon" />
          )}
          <h2>Mi Perfil</h2>
        </div>
      </div>
      
      {isUsingCache && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ⚡ Datos cargados desde caché - Carga instantánea
          </Typography>
        </Alert>
      )}
      
      <form onSubmit={handleSubmit} className="form-container">
        {formFields.map((field) => (
          <div key={field.name} className="field-container">
            {field.name === 'foto' ? (
              <>
                <label>{field.label}</label>
                <div className="upload-button">
                  <ImageUploader file={file} setFile={onFileChange} />
                  <input
                    type="file"
                    name={field.name}
                    onChange={handleInputChange}
                  />
                </div>
              </>
            ) : (
              <>
                {field.name === 'tipoUsuario' ? (
                  <div className="field-container">
                    <label>{field.label}</label>
                    <Autocomplete
                      multiple
                      id="tipoUsuario"
                      options={['administrador', 'administrativo', 'inspector']}
                      value={userData.tipoUsuario || []}
                      onChange={(event, newValue) => setUserData((prevData) => ({ ...prevData, tipoUsuario: newValue }))}
                      renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                    />
                  </div>
                ) : (
                  <TextField
                    label={field.label}
                    name={field.name}
                    value={userData[field.name] || ''}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                  />
                )}
              </>
            )}
          </div>
        ))}
        
        {/* Sección de Clientes con Lazy Loading */}
        <div style={{ marginBottom: '16px' }}>
          <Button
            variant="outlined"
            onClick={() => setShowClientes(!showClientes)}
            endIcon={showClientes ? <ExpandLess /> : <ExpandMore />}
            fullWidth
            style={{ 
              justifyContent: 'space-between', 
              textTransform: 'none',
              borderColor: clientesValues.length > 0 ? '#1976d2' : '#ddd',
              backgroundColor: clientesValues.length > 0 ? '#f0f8ff' : 'transparent'
            }}
          >
            <span>Clientes Asignados</span>
            <span style={{ 
              fontSize: '12px', 
              color: clientesValues.length > 0 ? '#1976d2' : '#666',
              fontWeight: clientesValues.length > 0 ? '500' : 'normal'
            }}>
              {clientesValues.length} seleccionados
            </span>
          </Button>
          
          <Collapse in={showClientes}>
            <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
              {/* Campo de búsqueda */}
              <TextField
                fullWidth
                placeholder="Buscar clientes por nombre..."
                value={clientesSearchTerm}
                onChange={(e) => setClientesSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: clientesSearchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setClientesSearchTerm('')}
                        edge="end"
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                size="small"
                style={{ marginBottom: '12px' }}
              />
              
              {/* Información de resultados */}
              <div style={{ 
                marginBottom: '12px', 
                padding: '8px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px',
                fontSize: '12px',
                color: '#666'
              }}>
                {clientesSearchDebounce 
                  ? `${clientesMostrados.length} clientes encontrados • ${clientesValues.length} asignados`
                  : `${clientesValues.length} clientes asignados • Usa la búsqueda para agregar más clientes`
                }
              </div>
              
              {/* Opciones de selección múltiple */}
              <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Typography variant="caption" style={{ color: '#666', fontWeight: '500' }}>
                    Selección múltiple:
                  </Typography>
                </div>
                
                {/* Opción 2: Asignar TODOS los clientes de la base de datos */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Checkbox
                    checked={clientesValues.length > 0 && clientesData.length > 0 && clientesData.every(c => c.isAsignado)}
                    onChange={(e) => handleAssignAllClientes(e.target.checked)}
                    size="small"
                  />
                  <span style={{ fontSize: '13px', color: '#d32f2f' }}>
                    🚀 Asignar TODOS los clientes de la base de datos
                  </span>
                </div>
              </div>
              
              {/* Lista de clientes */}
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {clientesMostrados.map((cliente) => (
                  <div key={cliente.id} style={{ 
                    padding: '8px', 
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: cliente.isAsignado ? '#f0f8ff' : 'transparent',
                    borderLeft: cliente.isAsignado ? '3px solid #1976d2' : 'none'
                  }}>
                    <Checkbox
                      checked={cliente.isAsignado}
                      onChange={(e) => handleChangeClientes(
                        e.target.checked 
                          ? [...clientesValues, cliente.id]
                          : clientesValues.filter(id => id !== cliente.id)
                      )}
                      size="small"
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ 
                        fontSize: '13px',
                        fontWeight: cliente.isAsignado ? '500' : 'normal',
                        color: cliente.isAsignado ? '#1976d2' : 'inherit'
                      }}>
                        {cliente.nombre}
                        {cliente.comunidad && ` (${cliente.comunidad})`}
                      </span>
                      {cliente.isAsignado && (
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#1976d2',
                          marginTop: '2px'
                        }}>
                          ✓ Asignado
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Loading indicator */}
                {clientesLoading && (
                  <div style={{ textAlign: 'center', padding: '12px' }}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" display="block">
                      Cargando más clientes...
                    </Typography>
                  </div>
                )}
                
                {/* Observer target para infinite scroll */}
                {clientesHasMore && !clientesLoading && (
                  <div ref={clientesObserverRef} style={{ height: '20px' }} />
                )}
                
                {/* Mensaje de fin */}
                {!clientesHasMore && clientesMostrados.length > 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '12px', 
                    color: '#666',
                    fontSize: '12px',
                    borderTop: '1px solid #eee',
                    marginTop: '8px'
                  }}>
                    No hay más clientes que mostrar
                  </div>
                )}
                
                {/* Mensaje cuando no hay datos */}
                {clientesMostrados.length === 0 && !clientesLoading && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px', 
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    {clientesSearchDebounce 
                      ? `No se encontraron clientes que coincidan con "${clientesSearchDebounce}"`
                      : "Busca clientes para ver los disponibles"
                    }
                  </div>
                )}
              </div>
            </div>
          </Collapse>
        </div>


        
        {/* Sección de Servicios con Lazy Loading */}
        <div style={{ marginBottom: '16px' }}>
          <Button
            variant="outlined"
            onClick={() => setShowServicios(!showServicios)}
            endIcon={showServicios ? <ExpandLess /> : <ExpandMore />}
            fullWidth
            style={{ 
              justifyContent: 'space-between', 
              textTransform: 'none',
              borderColor: tiendasValues.length > 0 ? '#1976d2' : '#ddd',
              backgroundColor: tiendasValues.length > 0 ? '#f0f8ff' : 'transparent'
            }}
          >
            <span>Servicios Asignados</span>
            <span style={{ 
              fontSize: '12px', 
              color: tiendasValues.length > 0 ? '#1976d2' : '#666',
              fontWeight: tiendasValues.length > 0 ? '500' : 'normal'
            }}>
              {tiendasValues.length} seleccionados
            </span>
          </Button>
          
          <Collapse in={showServicios}>
            <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
              {/* Campo de búsqueda */}
              <TextField
                fullWidth
                placeholder="Buscar servicios por número o nombre..."
                value={serviciosSearchTerm}
                onChange={(e) => setServiciosSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: serviciosSearchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setServiciosSearchTerm('')}
                        edge="end"
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                size="small"
                style={{ marginBottom: '12px' }}
              />
              
              {/* Información de resultados */}
              <div style={{ 
                marginBottom: '12px', 
                padding: '8px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px',
                fontSize: '12px',
                color: '#666'
              }}>
                {serviciosSearchDebounce 
                  ? `${serviciosMostrados.length} servicios encontrados • ${tiendasValues.length} asignados`
                  : `${tiendasValues.length} servicios asignados • Usa la búsqueda para agregar más servicios`
                }
              </div>
              
              {/* Toggle de visualización Localización/Nombre */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Mostrar:
                </Typography>
                <ToggleButtonGroup
                  value={serviciosDisplayMode}
                  exclusive
                  onChange={handleServiciosDisplayModeChange}
                  size="small"
                >
                  <ToggleButton value="localizacion" sx={{ textTransform: 'none', px: 2 }}>
                    Localización
                  </ToggleButton>
                  <ToggleButton value="nombre" sx={{ textTransform: 'none', px: 2 }}>
                    Nombre
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Opciones de selección múltiple */}
              <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Typography variant="caption" style={{ color: '#666', fontWeight: '500' }}>
                    Selección múltiple:
                  </Typography>
                </div>
                
                {/* Opción 2: Asignar TODOS los servicios de la base de datos */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Checkbox
                    checked={tiendasValues.length > 0 && serviciosData.length > 0 && serviciosData.every(s => s.isAsignado)}
                    onChange={(e) => handleAssignAllServicios(e.target.checked)}
                    size="small"
                  />
                  <span style={{ fontSize: '13px', color: '#d32f2f' }}>
                    🚀 Asignar TODOS los servicios de la base de datos
                  </span>
                </div>
              </div>
              
              {/* Lista de servicios */}
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {serviciosMostrados.map((servicio) => (
                  <div key={servicio.id} style={{ 
                    padding: '8px', 
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: servicio.isAsignado ? '#f0f8ff' : 'transparent',
                    borderLeft: servicio.isAsignado ? '3px solid #1976d2' : 'none'
                  }}>
                    <Checkbox
                      checked={servicio.isAsignado}
                      onChange={(e) => handleChangeTiendas(
                        e.target.checked 
                          ? [...tiendasValues, servicio.id]
                          : tiendasValues.filter(id => id !== servicio.id)
                      )}
                      size="small"
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ 
                        fontSize: '13px',
                        fontWeight: servicio.isAsignado ? '500' : 'normal',
                        color: servicio.isAsignado ? '#1976d2' : 'inherit'
                      }}>
                        {serviciosDisplayMode === 'localizacion' && servicio.localizacion 
                          ? servicio.localizacion 
                          : servicio.numTienda} - {servicio.nombreTienda}
                        {servicio.nombreCliente && ` (${servicio.nombreCliente})`}
                      </span>
                      {serviciosDisplayMode === 'nombre' && servicio.localizacion && (
                        <span style={{ fontSize: '10px', color: '#888', marginLeft: '8px' }}>
                          📍 {servicio.localizacion}
                        </span>
                      )}
                      {servicio.isAsignado && (
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#1976d2',
                          marginTop: '2px'
                        }}>
                          ✓ Asignado
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Loading indicator */}
                {serviciosLoading && (
                  <div style={{ textAlign: 'center', padding: '12px' }}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" display="block">
                      Cargando más servicios...
                    </Typography>
                  </div>
                )}
                
                {/* Observer target para infinite scroll */}
                {serviciosHasMore && !serviciosLoading && (
                  <div ref={serviciosObserverRef} style={{ height: '20px' }} />
                )}
                
                {/* Mensaje de fin */}
                {!serviciosHasMore && serviciosMostrados.length > 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '12px', 
                    color: '#666',
                    fontSize: '12px',
                    borderTop: '1px solid #eee',
                    marginTop: '8px'
                  }}>
                    No hay más servicios que mostrar
                  </div>
                )}
                
                {/* Mensaje cuando no hay datos */}
                {serviciosMostrados.length === 0 && !serviciosLoading && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px', 
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    {serviciosSearchDebounce 
                      ? `No se encontraron servicios que coincidan con "${serviciosSearchDebounce}"`
                      : "Busca servicios para ver los disponibles"
                    }
                  </div>
                )}
              </div>
            </div>
          </Collapse>
        </div>
        

        
        <FormControl fullWidth>
          <InputLabel id="comunidades-label">Comunidades</InputLabel>
          <Select
            labelId="comunidades-label"
            id="comunidades"
            multiple
            value={userData.comunidadesAsignadas || []}
            onChange={handleChangeComunidades}
            label="Comunidades"
          >
            {comunidades.map((comunidad) => (
              <MenuItem key={comunidad} value={comunidad}>
                {comunidad}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ProvinciasSelect
          comunidadesSeleccionadas={userData.comunidadesAsignadas || []}
          provinciasSeleccionadas={userData.provinciasAsignadas || []}
          onSelectProvincias={handleSelectProvincias}
        />
        <Button 
          variant="contained" 
          type="submit" 
          className="small"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              Guardando...
              <CircularProgress size={20} style={{ marginLeft: '10px', color: '#fff' }} />
            </>
          ) : (
            'Guardar Cambios'
          )}
        </Button>
      </form>
      <AlertComponent />
    </div>
  );
};

export default Profile;